import * as THREE from 'three';
import { handleSleighMovement } from './movement.js';
import { handleSleighCollisions, handleLasers } from './collisions.js';
import { updateSingleSnowball, updateSingleTree, createSnowball, createTree, updateSinglePresent, createPresent } from './spawn.js';
import { SpatialGrid } from './spatialGrid.js';
import gsap from 'gsap';
import { textureManager } from './textures.js';
import { Sound } from './sounds.js';

export class Game {
    constructor() {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 100);
        this.camera.position.set(0, 0, 5);
        this.scene.add(this.camera);

        this.renderer = new THREE.WebGLRenderer({ canvas: document.querySelector('canvas.webgl') });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        this.sleigh = null; 
        this.snowballs = [];
        this.trees = [];
        this.lasers = [];
        this.presents = [];
        this.presentSpawnInterval = 10;
        this.lastPresentSpawnTime = 0;
        this.elapsedTime = 0;

        this.canShoot = true;
        this.grid = new SpatialGrid(5);
        this.canMove = true;

        this.maxHealth = 5;
        this.health = this.maxHealth;

        this.presentsCollected = 0;
        this.snowballsHit = 0;

        this.createHealthUI();
        this.createPresentsUI();
        this.createSnowballsUI();

        window.addEventListener('resize', () => this.onResize());

        this.laserMaterial = null;
        this.explosionMaterial = null;

        this.scene.fog = new THREE.FogExp2(0xffffff, 0.02);

        this.handleRestart = this.handleRestart.bind(this);
        this.endGameButtons();

        this.soundManager = new Sound();
        this.soundManager.attachListener(this.camera);
        this.soundManager.loadSound('laser', './sounds/laser.ogg');
        this.soundManager.loadSound('snowball', './sounds/snowballHit.ogg');
        this.soundManager.loadSound('presents', './sounds/presentCollected.ogg');
    }

    async loadAssets() {
        try {
            const { loadAllModels } = await import('./loaders.js');
            const { sleigh, snowball, tree, shaders, present } = await loadAllModels();
            this.sleigh = sleigh;
            this.baseSnowball = snowball;
            this.baseTree = tree;
            this.shaders = shaders;
            this.basePresent = present;
    
            this.scene.add(this.sleigh);
    
            await textureManager.loadAllTextures();
    
            this.createMaterials();
    
            this.scene.fog.color.set(0xffffff);
            this.scene.fog.density = 0.07;
        } catch (error) {
            console.error('Failed to load assets:', error);
        }
    }

    // Create Materials //
    createMaterials() {

        // Shader Material 
        this.groundMaterial = new THREE.ShaderMaterial({
            vertexShader: this.shaders.ground.vertex,
            fragmentShader: this.shaders.ground.fragment,
            uniforms: {
                uTime: { value: 0.0 },
                uFrequency: { value: 0.5 },
                uAmplitude: { value: 0.5 }
            }
        });

        // Snowflake Material 
        this.snowflakeMaterial = new THREE.ShaderMaterial({
            vertexShader: this.shaders.snowflake.vertex,
            fragmentShader: this.shaders.snowflake.fragment,
            uniforms: {
                uTime: { value: 0.0 },
                uSpeed: { value: 0.2 },
                uGroundHeight: { value: -3.0 }
            },
            transparent: true,
            depthTest: false,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });

        // Laser Material
        this.laserMaterial = new THREE.MeshBasicMaterial({
            map: textureManager.getTexture('laserColor'),
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthTest: false,
            depthWrite: false,
            color: 0xff0000
        });

        // Explosion Material
        this.explosionMaterial = new THREE.SpriteMaterial({
            map: textureManager.getTexture('explosion'),
            transparent: true,
            blending: THREE.AdditiveBlending
        });

        this.addGround();
        this.addSnowflakes();
    };

    // Add Ground //
    addGround() {
        const groundGeometry = new THREE.PlaneGeometry(50, 25, 25, 25);
        const groundMesh = new THREE.Mesh(groundGeometry, this.groundMaterial);
        groundMesh.rotation.x = -Math.PI / 2;
        groundMesh.position.y = -3;
        this.scene.add(groundMesh);
    };

    // Add snowflakes //
    addSnowflakes() {
        const particleCount = 300;
        const positions = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);
        const randomValues = new Float32Array(particleCount);

        for (let i = 0; i < particleCount; i++) {
            positions[i * 3] = (Math.random() - 0.5) * 10; 
            positions[i * 3 + 1] = 6;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 10;
            randomValues[i] = Math.random();
            sizes[i] = Math.random() * 1.5 + 0.5;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('aRandom', new THREE.BufferAttribute(randomValues, 1));
        geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));

        const particles = new THREE.Points(geometry, this.snowflakeMaterial);
        this.scene.add(particles);
    };

    // Spawn Presents //
    spawnPresent() {
        const present = createPresent(this.basePresent);
        this.scene.add(present);
        this.presents.push(present);
    };

    // Spawn Snowballs //
    spawnSnowballs(count) {
        for (let i = 0; i < count; i++) {
            const sb = createSnowball(this.baseSnowball);
            this.scene.add(sb);
            this.snowballs.push(sb);
        }
    };

    // Spawn Trees //
    spawnTrees(count) {
        for (let i = 0; i < count; i++) {
            const ts = createTree(this.baseTree);
            this.scene.add(ts);
            this.trees.push(ts);
        }
    };

    // Shoot Laser //
    shootLaser() {
        const laserGeometry = new THREE.PlaneGeometry(0.75, 0.5); 
        const laser = new THREE.Mesh(laserGeometry, this.laserMaterial);

        if (this.sleigh) {
        
            laser.position.copy(this.sleigh.position);

            const offset = new THREE.Vector3(1, 0, 0);
            this.sleigh.localToWorld(offset);
            laser.position.set(offset.x, offset.y, offset.z);

            laser.position.y += 0.1;
            laser.position.x += 0.8;
        }
        this.scene.add(laser);
        this.lasers.push(laser);

        this.soundManager.playSound('laser');

    };

    // Snowball Collision -- Shake Effect
    handleSnowballCollision() {
        this.applyDamage();
        this.canMove = false;
        if (typeof gsap !== 'undefined') {
            gsap.to(this.sleigh.position, {
                x: `+=0.1`,
                y: `+=0.05`,
                duration: 0.05,
                repeat: 5,
                yoyo: true,
                onComplete: () => {
                    this.canMove = true;
                }
            });
        }
        this.soundManager.playSound('snowball')
    };

    // Snowballs Hit UI //
    createSnowballsUI() {
        if (document.getElementById('snowballs-counter')) return;

        const snowballsCounter = document.createElement('div');
        snowballsCounter.id = 'snowballs-counter';

        const snowballsCount = document.createElement('span');
        snowballsCount.id = 'snowballs-count';
        snowballsCount.textContent = '0';
        snowballsCounter.appendChild(snowballsCount);

        document.body.appendChild(snowballsCounter);
    }

    updateSnowballsUI() {
        const snowballsCount = document.getElementById('snowballs-count');
        if (!snowballsCount) return;
        snowballsCount.textContent = this.snowballsHit;
    }

    handleSnowballHitByLaser() {
        this.snowballsHit += 1;
        this.updateSnowballsUI();
        this.soundManager.playSound('snowball');
    }

    addSnowballsScore(points) {
        if (!this.score) this.score = 0; 
        this.score += points;
    }

    // Collect Presents UI //
    createPresentsUI() {
        if (document.getElementById('presents-counter')) return;

        const presentsCounter = document.createElement('div');
        presentsCounter.id = 'presents-counter';

        const presentsCount = document.createElement('span');
        presentsCount.id = 'presents-count';
        presentsCount.textContent = '0'; 
        presentsCounter.appendChild(presentsCount);

        document.body.appendChild(presentsCounter);
    }

    updatePresentsUI() {
        const presentsCount = document.getElementById('presents-count');
        if (!presentsCount) return;
        presentsCount.textContent = this.presentsCollected; 
    }

    handlePresentCollision() {
        this.addPresentsScore(1);
        this.presentsCollected += 1;
        this.updatePresentsUI();
        this.soundManager.playSound('presents');

    }
    
    addPresentsScore(points) {
        if (!this.score) this.score = 0; 
        this.score += points;
    }

    // Health Bar //
    updateHealthBar() {
        const healthFill = document.getElementById('health-fill');
        if (!healthFill) return; 
        const percentage = (this.health / this.maxHealth); 
        healthFill.style.transform = `scaleX(${percentage})`;
    }

    // End Game // 
    endGame() {
        this.canMove = false;
        this.canShoot = false;

        const overlay = document.getElementById('endgame-overlay')
        if(overlay){
            document.getElementById('final-presents').textContent = `Presents Collected: ${this.presentsCollected}`;
            document.getElementById('final-snowballs').textContent = `Snowballs Hit: ${this.snowballsHit}`;

            overlay.classList.remove('hidden');
            overlay.classList.add('visible');
        }
    }

    endGameButtons() {
        const restartButton = document.getElementById('restart-button');

        if (restartButton) {
            restartButton.addEventListener('click', this.handleRestart);
        }
    }

    handleRestart(){
        location.reload();
    }

    applyDamage() {
        this.health--;
        if (this.health <= 0) {
            this.health = 0;
            this.endGame();
        }
        this.updateHealthBar();
    }

    update(deltaTime, keysPressed) {
        if(!this.canMove && !this.canShoot) {
            return;
        }

        this.elapsedTime += deltaTime;
        if (this.elapsedTime - this.lastPresentSpawnTime > this.presentSpawnInterval) {
            this.spawnPresent();
            this.lastPresentSpawnTime = this.elapsedTime;
        }

        // Update uniforms for shader animation
        if (this.groundMaterial && this.groundMaterial.uniforms.uTime) {
            this.groundMaterial.uniforms.uTime.value += deltaTime;
        }
        if (this.snowflakeMaterial && this.snowflakeMaterial.uniforms.uTime) {
            this.snowflakeMaterial.uniforms.uTime.value += deltaTime;
        }

        // Update movements
        if(this.canMove) {
            handleSleighMovement(keysPressed, this.sleigh, deltaTime)
        };
        handleLasers(this.lasers, this.snowballs, this.scene, this, deltaTime);

        // Check if shoot-key is pressed
        if (keysPressed.shoot && this.canShoot) {
            this.shootLaser();
            this.canShoot = false;
            setTimeout(() => {
                this.canShoot = true;
            }, 250);
        }

        // Update object positions
        this.snowballs.forEach(sb => updateSingleSnowball(sb, deltaTime));
        this.trees.forEach(ts => updateSingleTree(ts, deltaTime));
        this.presents.forEach(present => updateSinglePresent(present, deltaTime));

        // Populate Spatial Grid
        this.grid.clear();
        this.snowballs.forEach(sb => this.grid.addObject(sb));
        this.trees.forEach(ts => this.grid.addObject(ts));

        // this.grid.addObject(this.sleigh);

        // Use spatial grid for collisions
        const nearbyObjects = this.grid.getNearbyObjects(this.sleigh.position);
        handleSleighCollisions(
            this.sleigh, 
            nearbyObjects.filter(o => this.snowballs.includes(o)),
            this.presents,
            () => this.handleSnowballCollision(),            
            () => this.handlePresentCollision(),            
            this.scene,
            this.explosionMaterial,
            this
        );
    }

    render() {
        this.renderer.render(this.scene, this.camera);
    }

    createHealthUI() {
        if (document.getElementById('health-container')) return;
        const healthContainer = document.createElement('div');
        healthContainer.id = 'health-container';
        document.body.appendChild(healthContainer);

        const healthFill = document.createElement('div');
        healthFill.id = 'health-fill';
        healthContainer.appendChild(healthFill);
    }

    onResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    }
}

